import { inject, Injectable, Injector } from '@angular/core';
import { CombinedIds } from '@features/digital-venue/services/seat-management.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SeatsBySection } from '../models';
import { Seat } from '../models/seat.model';
import { DvmService } from './dvm.service';

@Injectable({
  providedIn: 'root',
})
export class UtilitiesService {
  isMobile: boolean;
  isTablet: boolean;
  isDesktopDevice: boolean;
  device: {
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
  ssoRoute?: string;

  /**
   * Translate node ids, from mmc id to tdc id
   */
  public nodeTranslator: Map<string, string> = new Map();

  /**
   * Translate node ids, from tdc_id to mmc_id
   */
  public tdcToMmcTranslator: Map<string, string> = new Map();

  private deviceService = inject(DeviceDetectorService);
  private injector = inject(Injector);

  constructor() {
    this.deviceDetector();
  }

  deviceDetector() {
    this.isMobile = this.deviceService.isMobile();
    this.isTablet = this.deviceService.isTablet();
    this.isDesktopDevice = this.deviceService.isDesktop();

    this.device = {
      mobile: this.isMobile,
      tablet: this.isTablet,
      desktop: this.isDesktopDevice,
    };
  }

  distributeSeatsBySection(seats: Seat[]) {
    let seatsBySection: { [key: string]: SeatsBySection } = {};

    for (const seat of seats) {
      if (!seatsBySection[seat.section || seat.original_id]) {
        seatsBySection[seat.section || seat.original_id] = {
          id: seat.id.split('-')[0],
          original_id: seat.original_id,
          seats: {},
          totalFee: 0,
          totalSectionPrice: 0,
          totalSectionPriceWithFee: 0,
          totalSectionSeats: 0,
        };
      }
      const sectionKey = seat.section || seat.original_id;
      if (sectionKey) {
        (seatsBySection[sectionKey].seats as { [key: string]: Seat })[seat.id] =
          seat;
      }
    }

    return seatsBySection;
  }

  sortSectionsBySeatsQuantity(
    seatsBySection: SeatsBySection | Record<string, SeatsBySection>
  ) {
    let seatsBySectionArray: SeatsBySection[] = Object.values(seatsBySection);

    this.sectionsTotalSeatsCount(seatsBySectionArray);
    this.sectionsTotalPriceCount(seatsBySectionArray);

    if (seatsBySectionArray.length > 1) {
      // Sorting Sections if there are more than one with selected Seats
      let sortedSeatsBySectionArray = seatsBySectionArray.sort((a, b) => {
        return b.totalSectionSeats - a.totalSectionSeats;
      });
      return sortedSeatsBySectionArray;
    } else {
      return seatsBySectionArray;
    }
  }

  addRowDistribution(seatsBySectionArray: SeatsBySection[]): SeatsBySection[] {
    for (const section of seatsBySectionArray) {
      let sectionSeats: Seat[] = Object.values(section.seats);
      let seatsByRow: {
        [key: string]: {
          id: string;
          seats: {
            [key: string]: Seat;
          };
          seatsList: string[];
        };
      } = {};

      for (const seat of sectionSeats) {
        if (!seatsByRow[seat.seat_row as string]) {
          seatsByRow[seat.seat_row as string] = {
            id: seat.seat_row as string,
            seats: {},
            seatsList: [],
          };
        }
        seatsByRow[seat.seat_row as string].seats[seat.id] = seat;
        seatsByRow[seat.seat_row as string].seatsList.push(seat.seat as string);
      }

      section.rows = seatsByRow as any;
    }

    return seatsBySectionArray;
  }

  /**
   * Stores every seat number of each row, sorted numerically (no ranges). The seat
   * id format is `SECTION-ROW-SEAT`.
   */
  addSeatNumbers(seatsBySectionAndRowArray: SeatsBySection[]) {
    seatsBySectionAndRowArray.forEach(section => {
      if (!section.rows) {
        return;
      }
      section.rows.forEach(row => {
        const seatNumbers = row.seatsList.map(seat => seat.split('-')[2]);
        row.seatsNumbers = seatNumbers.sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true })
        );
      });
    });

    return seatsBySectionAndRowArray;
  }

  sectionsTotalSeatsCount(seatsBySectionArray: SeatsBySection[]) {
    for (const section of seatsBySectionArray) {
      section.totalSectionSeats = Object.keys(section.seats).length;
    }
  }

  sectionsTotalPriceCount(seatsBySectionArray: SeatsBySection[]) {
    for (const section of seatsBySectionArray) {
      section.totalSectionPrice = Object.values(section.seats).reduce(
        (total, seat) => total + seat['price'],
        0
      );
      section.totalSectionPriceWithFee = Object.values(section.seats).reduce(
        (total, seat) => total + seat['totalPriceWithFees'],
        0
      );

      section.totalFee = Object.values(section.seats).reduce(
        (total, seat) => total + seat['totalFee'],
        0
      );
    }
  }

  splittedSeatInfoFromId(seatIds: CombinedIds | string) {
    // "S_310-8-9"
    let seatInfo: {
      id: string;
      section: string;
      seat_row: string;
      seat: string;
      original_id?: string;
    };
    const isString = typeof seatIds === 'string';

    const splitedInfo = isString
      ? seatIds.split('-')
      : seatIds.original_id.split('-');

    // On general admission, the id received is the TDC one, so we get the MMC one from the service
    let mmcSeatId: string = '';
    if (isString) {
      const dvmService = this.injector.get(DvmService);
      mmcSeatId =
        (dvmService.translateSectionTdcId(seatIds) as string) ??
        (dvmService.translateSectionTdcId(splitedInfo[0]) as string);
    }

    seatInfo = {
      id: isString ? seatIds : seatIds.id,
      section: splitedInfo[0],
      seat_row: splitedInfo[1],
      seat: isString ? seatIds : splitedInfo[2],
      original_id: isString ? mmcSeatId : seatIds?.original_id,
    };

    return seatInfo;
  }

  totalSumApiSeatsPrice(seats: Seat[]): number {
    let sumPrice = seats.reduce(
      (accumulator, current) => accumulator + current.price,
      0
    );
    return sumPrice;
  }

  totalSumDvmSeatsPrices(seats: { [key: string]: Seat }) {
    let totalFee = 0;
    let totalPrice = 0;
    let totalPriceWithFees = 0;

    Object.values(seats).forEach(seat => {
      totalPrice += seat.price;
      totalFee += seat.totalFee;
      totalPriceWithFees += seat.totalPriceWithFees;
    });

    return { totalFee, totalPrice, totalPriceWithFees };
  }

  formatRgb(rgbString: string, wrapped = true) {
    if (rgbString === 'transparent') {
      return rgbString;
    }

    return wrapped
      ? 'rgb(' + rgbString.split(' ').join(', ') + ')'
      : rgbString.split(' ').join(', ');
  }
}
